import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { getLevelData } from '../../utils/cbcUtils';
import { 
    generatePerformanceMatrixPDF, 
    generateDetailedReportPDF 
} from '../../utils/pdfGenerator';
import {
    UserGroupIcon,
    MagnifyingGlassIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    EyeIcon,
    FunnelIcon,
    ArrowLeftIcon,
    ArrowPathIcon,
    ArrowDownTrayIcon,
    ChevronRightIcon as ChevronNext,
    XMarkIcon,
    TrashIcon,
    ExclamationTriangleIcon,
    UsersIcon,
    ListBulletIcon,
    TableCellsIcon
} from '@heroicons/react/24/outline';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { Combobox, ComboboxInput, ComboboxOptions, ComboboxOption } from '@headlessui/react';
import FilterDropdown from '../../components/common/FilterDropdown';

const ScholarRoster = ({ isHOS = false }) => {
    const { get, post } = useApi();
    const { showToast, is_hod, is_principal } = useAppState();
    const navigate = useNavigate();
    const location = useLocation();
    const { state } = location;

    const schoolLevelId = state?.schoolLevelId;
    const levelId = state?.levelId; // Backward compatibility or single grade
    const streamId = state?.streamId;

    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('list'); // 'list' | 'matrix'
    const [matrixData, setMatrixData] = useState(null);
    const [matrixLoading, setMatrixLoading] = useState(false);
    const [students, setStudents] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [performanceFilter, setPerformanceFilter] = useState('');
    const [attendanceFilter, setAttendanceFilter] = useState('');
    const [pathwayFilter, setPathwayFilter] = useState('');
    const [selectedParents, setSelectedParents] = useState({}); // studentId -> relationship (FATHER/MOTHER)
    const [isExporting, setIsExporting] = useState(false);
    const [isFormatModalOpen, setIsFormatModalOpen] = useState(false);
    const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
    const [studentToRemove, setStudentToRemove] = useState(null);
    const [isRemoving, setIsRemoving] = useState(false);
    const [exportProgress, setExportProgress] = useState({ active: false, current: 0, total: 0, studentName: '' });
    // Export modal 2-step state
    const [exportStep, setExportStep] = useState('format'); // 'format' | 'filters'
    const [exportFormat, setExportFormat] = useState(null); // 'pdf' | 'excel' | 'zip'
    const [exportFilters, setExportFilters] = useState({
        termId: '',
        careerPathway: '',
        performanceLevel: '',
        attendanceLevel: '',
        scope: 'all', // 'all' | 'filtered'
        selectedStudentIds: [],
        houseId: '',
        reportType: 'all',
        learningAreaId: '',
        reportPeriod: 'end_term',
        examType: '',
        classFilter: '',
    });
    const [terms, setTerms] = useState([]);
    const [houses, setHouses] = useState([]);
    const [learningAreas, setLearningAreas] = useState([]);
    const [houseFilter, setHouseFilter] = useState('');
    const [comboboxQuery, setComboboxQuery] = useState('');
    
    const [showFilters, setShowFilters] = useState(false);
    const [termFilter, setTermFilter] = useState('');
    const [examTypeFilter, setExamTypeFilter] = useState('');
    const [classFilter, setClassFilter] = useState(''); // Combined Grade/Stream filter

    // Balance map: {student_id: balance | null}
    const [balanceMap, setBalanceMap] = useState({});

    // Extract unique classes from fetched students for Grade/Stream dropdown
    // Shown to HOS, HOD, and Principal
    const canFilterByClass = isHOS || is_hod || is_principal;
    const classOptions = useMemo(() => {
        if (!canFilterByClass) return [];
        const unique = new Set();
        students.forEach(s => {
            if (s.grade_level_name) {
                unique.add(s.grade_level_name); // Just the grade
                if (s.stream_name) {
                    unique.add(`${s.grade_level_name} ${s.stream_name}`); // Grade + Stream
                }
            }
        });
        return Array.from(unique).sort().map(c => ({ name: c, value: c }));
    }, [students, canFilterByClass]);

    const itemsPerPage = 15;

    const handleExportPDF = async (filters = {}) => {
        try {
            setIsFormatModalOpen(false);
            setIsExporting(true);
            showToast('Preparing performance matrix...', 'info');
            
            const dataParams = new URLSearchParams();
            if (schoolLevelId) dataParams.append('school_level', schoolLevelId);
            else if (levelId) dataParams.append('grade_level', levelId);
            if (streamId) dataParams.append('stream_id', streamId);
            if (filters.termId) dataParams.append('term_id', filters.termId);
            if (filters.careerPathway) dataParams.append('career_pathway', filters.careerPathway);
            if (filters.performanceLevel) dataParams.append('performance_level', filters.performanceLevel);
            if (filters.attendanceLevel) dataParams.append('attendance_filter', filters.attendanceLevel);
            if (filters.houseId) dataParams.append('house_id', filters.houseId);
            if (filters.examType) dataParams.append('exam_type', filters.examType);
            
            const matrixData = await get(`/api/teacher/class-teacher/performance_matrix/?${dataParams.toString()}`);
            
            if (!matrixData || !matrixData.students || matrixData.students.length === 0) {
                showToast('No performance data available for this class.', 'warning');
                return;
            }

            await generatePerformanceMatrixPDF(matrixData);
            showToast('Performance Matrix exported!', 'success');
        } catch (error) {
            console.error('PDF Export failed:', error);
            showToast('Failed to export matrix PDF', 'error');
        } finally {
            setIsExporting(false);
        }
    };

    const handleExportExcel = async (filters = {}) => {
        try {
            setIsFormatModalOpen(false);
            setIsExporting(true);
            showToast('Generating Excel matrix...', 'info');
            
            const dataParams = new URLSearchParams();
            if (schoolLevelId) dataParams.append('school_level', schoolLevelId);
            else if (levelId) dataParams.append('grade_level', levelId);
            if (streamId) dataParams.append('stream_id', streamId);
            if (filters.termId) dataParams.append('term_id', filters.termId);
            if (filters.careerPathway) dataParams.append('career_pathway', filters.careerPathway);
            if (filters.performanceLevel) dataParams.append('performance_level', filters.performanceLevel);
            if (filters.attendanceLevel) dataParams.append('attendance_filter', filters.attendanceLevel);
            if (filters.houseId) dataParams.append('house_id', filters.houseId);
            if (filters.examType) dataParams.append('exam_type', filters.examType);
            if (filters.classFilter) dataParams.append('class_filter', filters.classFilter);
            
            if (filters.learningAreaId) {
                dataParams.append('learning_area_id', filters.learningAreaId);
            }

            const url = `/api/teacher/class-teacher/export_performance_matrix_excel/?${dataParams.toString()}`;
            
            const finalUrl = window.location.port === '3000' 
                ? `http://${window.location.hostname}:8000${url}`
                : url;

            const finalResponse = await fetch(finalUrl, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                }
            });

            if (!finalResponse.ok) throw new Error('Export failed');

            const blob = await finalResponse.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            
            const contentDisposition = finalResponse.headers.get('Content-Disposition');
            let filename = "Performance_Matrix.xlsx";
            if (contentDisposition && contentDisposition.includes('filename=')) {
                filename = contentDisposition.split('filename=')[1].replace(/"/g, '');
            }
            
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(downloadUrl);
            document.body.removeChild(a);
            
            showToast('Excel Matrix downloaded!', 'success');
        } catch (error) {
            console.error('Excel Export failed:', error);
            showToast('Failed to export Excel matrix', 'error');
        } finally {
            setIsExporting(false);
        }
    };


    const handleExportZip = async (filters = {}) => {
        try {
            setIsFormatModalOpen(false);
            setIsExporting(true);
            
            // Determine which students to export
            let studentsToExport = [];
            
            if (filters.selectedStudentIds && filters.selectedStudentIds.length > 0) {
                // If specific students were selected in the searchable dropdown, use only those.
                studentsToExport = students.filter(s => filters.selectedStudentIds.includes(s.id));
            } else {
                // Fallback to scope logic
                studentsToExport = filters.scope === 'filtered' ? filteredStudents : students;
                
                // Apply performance threshold filter if set
                if (filters.performanceLevel) {
                    const levelOrder = { BE: 1, AE: 2, ME: 3, EE: 4 };
                    const threshold = levelOrder[filters.performanceLevel] || 0;
                    studentsToExport = studentsToExport.filter(s => {
                        const lvl = (s.average_performance || '').substring(0, 2).toUpperCase();
                        return (levelOrder[lvl] || 0) <= threshold;
                    });
                }

                // Apply House filter if set
                if (filters.houseId) {
                    studentsToExport = studentsToExport.filter(s => s.house_id === parseInt(filters.houseId));
                }
            }

            if (studentsToExport.length === 0) {
                showToast('No students match the export criteria', 'warning');
                setIsExporting(false);
                return;
            }

            // Optimization: If only 1 student is selected, download directly without ZIP
            if (studentsToExport.length === 1) {
                const student = studentsToExport[0];
                setIsFormatModalOpen(false);
                setIsExporting(true);
                showToast(`Generating report for ${student.name}...`, 'info');
                
                const activeTermResp = await get('/api/auth/active-term/');
                const termId = filters.termId || activeTermResp?.term?.id;
                
                const reportData = await get(`/api/cbc/reports/student/${student.id}/?term=${termId}&report_type=${filters.reportType}&report_period=${filters.reportPeriod || ''}`);
                await generateDetailedReportPDF(reportData, {
                    schoolName: 'Kianda School',
                    termLabel: reportData.term_label,
                    yearLabel: reportData.year_label,
                    reportPeriod: filters.reportPeriod,
                    reportType: filters.reportType,
                });
                
                showToast('Report downloaded!', 'success');
                setIsExporting(false);
                return;
            }

            // 1. Initial Setup
            setExportProgress({ active: true, current: 0, total: studentsToExport.length, studentName: 'Initializing...' });
            const zip = new JSZip();
            
            // 2. Resolve Term Context — use selected term or fall back to active term
            let termId, termName, yearName;
            if (filters.termId) {
                const selectedTerm = terms.find(t => t.id.toString() === filters.termId.toString());
                termId = filters.termId;
                termName = selectedTerm?.name || 'Term';
                yearName = selectedTerm?.year_name || new Date().getFullYear().toString();
            } else {
                const activeTermResp = await get('/api/auth/active-term/');
                termId = activeTermResp?.term?.id;
                termName = activeTermResp?.term?.name || 'Term';
                yearName = activeTermResp?.term?.year_name || new Date().getFullYear().toString();
            }

            if (!termId) {
                throw new Error('Could not resolve academic term for reports.');
            }

            // 3. Batch Process with Concurrency Limit (10 at a time)
            const concurrencyLimit = 10;
            const batches = [];
            for (let i = 0; i < studentsToExport.length; i += concurrencyLimit) {
                batches.push(studentsToExport.slice(i, i + concurrencyLimit));
            }

            setExportProgress(prev => ({ ...prev, current: 0 }));

            for (const batch of batches) {
                await Promise.all(batch.map(async (student) => {
                    try {
                        setExportProgress(prev => ({ ...prev, studentName: student.name }));
                        
                        // Fetch individual report data
                        const reportData = await get(`/api/cbc/reports/student/${student.id}/?term=${termId}&report_type=${filters.reportType}&report_period=${filters.reportPeriod || ''}`);
                        
                        // Generate PDF Blob
                        const pdfBlob = await generateDetailedReportPDF(reportData, {
                            schoolName: 'Kianda School',
                            termLabel: reportData.term_label || termName,
                            yearLabel: reportData.year_label || yearName,
                            reportPeriod: filters.reportPeriod,
                            reportType: filters.reportType,
                            returnType: 'blob'
                        });

                        const fileName = `Report_Card_${student.name.replace(/\s+/g, '_')}_${student.student_id}.pdf`;
                        zip.file(fileName, pdfBlob);
                        
                        setExportProgress(prev => ({ ...prev, current: prev.current + 1 }));
                    } catch (err) {
                        console.error(`Failed to export student ${student.name}:`, err);
                    }
                }));
            }

            // 4. Generate & Download ZIP
            setExportProgress(prev => ({ ...prev, studentName: 'Bundling ZIP Archive...' }));
            const archiveContent = await zip.generateAsync({ type: 'blob' });
            
            const zipName = `Bulk_Report_Cards_${termName.replace(/\s+/g, '_')}_${new Date().toLocaleDateString().replace(/\//g, '-')}.zip`;
            saveAs(archiveContent, zipName);

            showToast(`Successfully exported ${studentsToExport.length} report cards!`, 'success');
        } catch (error) {
            console.error('ZIP Export failed:', error);
            showToast(error.message || 'Failed to generate bulk report cards', 'error');
        } finally {
            setIsExporting(false);
            setExportProgress({ active: false, current: 0, total: 0, studentName: '' });
        }
    };

    const handleRemoveFromClass = useCallback((student) => {
        setStudentToRemove(student);
        setIsRemoveModalOpen(true);
    }, []);

    const confirmRemoveStudent = async () => {
        if (!studentToRemove) return;
        setIsRemoving(true);
        try {
            await post(`/api/teacher/class-teacher/remove_student/${studentToRemove.id}/`);
            showToast(`${studentToRemove.name} removed successfully`, 'success');
            setIsRemoveModalOpen(false);
            setStudentToRemove(null);
            fetchData();
        } catch (error) {
            console.error('Error removing student:', error);
            showToast('Failed to remove student', 'error');
        } finally {
            setIsRemoving(false);
        }
    };

    const initialCacheUsed = React.useRef(false);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            
            if (state?.cachedRoster && !initialCacheUsed.current) {
                setStudents(state.cachedRoster);
                initialCacheUsed.current = true;
                setLoading(false);
                return;
            }

            let url = isHOS ? '/api/teacher/hos/students/' : '/api/teacher/class-teacher/student_list/';
            const params = new URLSearchParams();
            if (schoolLevelId) params.append('school_level', schoolLevelId);
            else if (levelId) params.append('grade_level', levelId);
            
            if (streamId) params.append('stream_id', streamId);
            if (termFilter) params.append('term_id', termFilter); // Always pass termFilter
            if (examTypeFilter) params.append('exam_type', examTypeFilter);
            
            if (params.toString()) url += `?${params.toString()}`;

            const data = await get(url);
            setStudents(data);
        } catch (error) {
            console.error('Error fetching students:', error);
            showToast('Failed to load roster', 'error');
        } finally {
            setLoading(false);
        }
    }, [get, schoolLevelId, levelId, streamId, termFilter, examTypeFilter, showToast, isHOS, state?.cachedRoster]);

    const fetchMatrixData = useCallback(async () => {
        try {
            setMatrixLoading(true);
            const params = new URLSearchParams();
            if (schoolLevelId) params.append('school_level', schoolLevelId);
            else if (levelId) params.append('grade_level', levelId);
            
            if (streamId) params.append('stream_id', streamId);
            if (termFilter) params.append('term_id', termFilter);
            if (examTypeFilter) params.append('exam_type', examTypeFilter);
            if (pathwayFilter) params.append('career_pathway', pathwayFilter);
            if (houseFilter) params.append('house_id', houseFilter);
            
            const data = await get(`/api/teacher/class-teacher/performance_matrix/?${params.toString()}`);
            setMatrixData(data);
        } catch (error) {
            console.error('Error fetching matrix data:', error);
            showToast('Failed to load matrix data', 'error');
        } finally {
            setMatrixLoading(false);
        }
    }, [get, schoolLevelId, levelId, streamId, termFilter, examTypeFilter, pathwayFilter, houseFilter, showToast]);

    useEffect(() => {
        if (viewMode === 'list') {
            fetchData();
        } else if (viewMode === 'matrix') {
            fetchMatrixData();
        }
    }, [fetchData, fetchMatrixData, viewMode]);

    // Batch-fetch balances for Principal only once students are loaded
    useEffect(() => {
        if (!is_principal || students.length === 0) return;
        const ids = students.map(s => s.id).join(',');
        get(`/api/finance/batch-balances/?student_ids=${ids}`)
            .then(data => setBalanceMap(data || {}))
            .catch(() => {}); // Silently fail — balance is informational only
    }, [students, is_principal, get]);

    // Fetch terms for export modal
    useEffect(() => {
        const fetchTerms = async () => {
            try {
                const data = await get('/api/academic-terms/');
                if (Array.isArray(data)) {
                    setTerms(data);
                    if (!termFilter) {
                        const current = data.find(t => t.is_current);
                        if (current) setTermFilter(current.id);
                    }
                } else {
                    setTerms([]);
                }
            } catch (e) {
                console.warn('Could not load terms for export modal', e);
            }
        };
        fetchTerms();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [get]);

    // Fetch houses
    useEffect(() => {
        const fetchHouses = async () => {
            try {
                const data = await get('/api/houses/');
                setHouses(data);
            } catch (e) {
                console.warn('Could not load houses', e);
            }
        };
        fetchHouses();
    }, [get]);

    // Fetch learning areas
    useEffect(() => {
        const fetchLearningAreas = async () => {
            try {
                const data = await get('/api/cbc/learning-areas/');
                setLearningAreas(data);
            } catch (e) {
                console.warn('Could not load learning areas', e);
            }
        };
        fetchLearningAreas();
    }, [get]);

    const filteredStudents = useMemo(() => {
        return students.filter(student => {
            const matchesSearch = student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                student.student_id.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesPerformance = !performanceFilter || (student.average_performance && student.average_performance.startsWith(performanceFilter));

            const matchesAttendance = !attendanceFilter ||
                (attendanceFilter === 'low' ? student.attendance_percentage < 75 :
                    attendanceFilter === 'high' ? student.attendance_percentage >= 90 : true);

            const matchesPathway = !pathwayFilter || (student.career_pathway && student.career_pathway.includes(pathwayFilter));
            const matchesHouse = !houseFilter || student.house_id === parseInt(houseFilter);
            
            const studentClassStr = `${student.grade_level_name || ''} ${student.stream_name || ''}`.trim();
            const matchesClass = !classFilter || studentClassStr.startsWith(classFilter);

            return matchesSearch && matchesPerformance && matchesAttendance && matchesPathway && matchesHouse && matchesClass;
        }).sort((a, b) => (b.average_percentage || 0) - (a.average_percentage || 0));
    }, [students, searchTerm, performanceFilter, attendanceFilter, pathwayFilter, houseFilter, classFilter]);

    const totalPages = Math.ceil((filteredStudents?.length || 0) / itemsPerPage);
    const paginatedStudents = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return (filteredStudents || []).slice(start, start + itemsPerPage);
    }, [filteredStudents, currentPage]);


    if (loading) {
        return <LoadingSpinner fullScreen message="Fetching Grades..." />;
    }

    return (
        <div className="min-h-screen bg-slate-50">
            <div className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8 space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between bg-white rounded-[1.5rem] px-6 py-5 border border-slate-100 shadow-sm gap-4 mb-6">
                    <div className="flex items-center gap-4">
                        {!isHOS && (
                            <button
                                onClick={() => navigate(-1)}
                                className="p-2.5 bg-slate-50 text-slate-400 hover:text-[#18216D] hover:bg-slate-100 border border-slate-100 rounded-xl transition-all shadow-sm active:scale-95 group"
                            >
                                <ArrowLeftIcon className="w-5 h-5 group-hover:-translate-x-0.5 transition-transform" />
                            </button>
                        )}
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 bg-indigo-50 rounded-xl text-indigo-600">
                                <UsersIcon className="w-6 h-6" />
                            </div>
                            <div>
                                <h2 className="text-lg font-black text-[#18216D] uppercase tracking-tight leading-tight">
                                    Scholar Roster
                                </h2>
                                <p className="text-slate-500 font-medium text-xs mt-1">Manage and review your scholar records.</p>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 w-full md:w-auto">
                        <div className="relative group flex-1 min-w-[200px] max-w-xs">
                            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#18216D] transition-colors">
                                <MagnifyingGlassIcon className="w-4 h-4" />
                            </div>
                            <input
                                type="text"
                                placeholder="Search Name or ID..."
                                value={searchTerm}
                                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#18216D]/10 focus:border-[#18216D] transition-all text-xs font-bold text-[#18216D]"
                            />
                        </div>
                        
                        <div className="flex bg-slate-100 p-1 rounded-xl">
                            <button
                                onClick={() => setViewMode('list')}
                                className={`px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all ${
                                    viewMode === 'list' 
                                        ? 'bg-white text-[#18216D] shadow-sm font-bold' 
                                        : 'text-slate-500 hover:text-slate-700 font-medium'
                                }`}
                                title="List View"
                            >
                                <ListBulletIcon className="w-4 h-4" />
                                <span className="text-[10px] uppercase tracking-wider hidden sm:inline">List</span>
                            </button>
                            <button
                                onClick={() => setViewMode('matrix')}
                                className={`px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all ${
                                    viewMode === 'matrix' 
                                        ? 'bg-white text-[#18216D] shadow-sm font-bold' 
                                        : 'text-slate-500 hover:text-slate-700 font-medium'
                                }`}
                                title="Matrix View"
                            >
                                <TableCellsIcon className="w-4 h-4" />
                                <span className="text-[10px] uppercase tracking-wider hidden sm:inline">Matrix</span>
                            </button>
                        </div>
                        
                        <button
                            onClick={() => setIsFormatModalOpen(true)}
                            disabled={isExporting}
                            className={`p-2 rounded-xl transition-all border shadow-sm flex items-center gap-2 ${isExporting 
                                ? 'bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed' 
                                : 'bg-[#FFD700] text-[#18216D] font-black border-[#FFD700] hover:bg-[#FFC425]'}`}
                            title="Export Roster"
                        >
                            {isExporting ? <ArrowPathIcon className="w-5 h-5 animate-spin" /> : <ArrowDownTrayIcon className="w-5 h-5" />}
                            <span className="text-[10px] font-black uppercase tracking-widest hidden sm:inline">Export</span>
                        </button>

                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`p-2 rounded-xl transition-all border shadow-sm flex items-center gap-2 ${
                                showFilters 
                                    ? 'bg-[#18216D] text-white border-transparent' 
                                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                            }`}
                        >
                            <FunnelIcon className="w-5 h-5" />
                            <span className="text-[10px] font-black uppercase tracking-widest hidden sm:inline">Filters</span>
                        </button>
                        
                        <button
                            onClick={fetchData}
                            className="p-2 bg-white text-slate-400 hover:text-indigo-600 hover:bg-slate-50 border border-slate-200 rounded-xl transition-all shadow-sm active:scale-95"
                            title="Sync Data"
                        >
                            <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>

                {/* Filters Panel */}
                {showFilters && (
                    <div className="bg-white rounded-[1.5rem] p-5 shadow-sm border border-slate-100 mb-6 flex flex-wrap items-center gap-4 animate-in fade-in slide-in-from-top-2 duration-200">
                        <FilterDropdown
                            value={termFilter}
                            onChange={(val) => { setTermFilter(val); setCurrentPage(1); }}
                            options={terms.map(t => ({ name: t.name, value: t.id }))}
                            placeholder="Current Term"
                        />

                        <FilterDropdown
                            value={examTypeFilter}
                            onChange={(val) => { setExamTypeFilter(val); setCurrentPage(1); }}
                            options={[
                                { name: 'Combined (All)', value: '' },
                                { name: 'Mid-Term Only', value: 'mid-term' },
                                { name: 'End-Term Only', value: 'end-term' },
                            ]}
                            placeholder="Exam Type"
                        />

                        {canFilterByClass && (
                            <FilterDropdown
                                value={classFilter}
                                onChange={(val) => { setClassFilter(val); setCurrentPage(1); }}
                                options={classOptions}
                                placeholder="All Classes"
                            />
                        )}

                        <FilterDropdown
                            value={performanceFilter}
                            onChange={(val) => { setPerformanceFilter(val); setCurrentPage(1); }}
                            options={[
                                { name: 'Exceeding (EE)', value: 'EE' },
                                { name: 'Meeting (ME)', value: 'ME' },
                                { name: 'Approaching (AE)', value: 'AE' },
                                { name: 'Below (BE)', value: 'BE' },
                            ]}
                            placeholder="Performance"
                        />

                        <FilterDropdown
                            value={attendanceFilter}
                            onChange={(val) => { setAttendanceFilter(val); setCurrentPage(1); }}
                            options={[
                                { name: 'Low (<75%)', value: 'low' },
                                { name: 'High (>90%)', value: 'high' },
                            ]}
                            placeholder="Attendance"
                        />

                        <FilterDropdown
                            value={pathwayFilter}
                            onChange={(val) => { setPathwayFilter(val); setCurrentPage(1); }}
                            options={[
                                { name: 'STEM', value: 'STEM' },
                                { name: 'Social Sciences', value: 'SOCIAL_SCIENCES' },
                                { name: 'Arts & Sports', value: 'ARTS_SPORTS' },
                            ]}
                            placeholder="Pathways"
                        />

                        <FilterDropdown
                            value={houseFilter}
                            onChange={(val) => { setHouseFilter(val); setCurrentPage(1); }}
                            options={houses.map(h => ({ name: h.name, value: h.id }))}
                            placeholder="Houses"
                        />
                    </div>
                )}

                {/* Views */}
                {viewMode === 'list' ? (
                    <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-[#F8FAFC]">
                                <tr>
                                    <th className="px-8 py-5 text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Scholar</th>
                                    <th className="px-8 py-5 text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Attendance</th>
                                    <th className="px-8 py-5 text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Guardian Details</th>
                                    <th className="px-8 py-5 text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Pathway</th>
                                    <th className="px-8 py-5 text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Performance</th>
                                    {is_principal && (
                                        <th className="px-8 py-5 text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Balance</th>
                                    )}
                                    <th className="px-8 py-5 text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {paginatedStudents.length > 0 ? (
                                    paginatedStudents.map(student => (
                                        <tr key={student.id} className="hover:bg-slate-50/50 transition-colors group">
                                            <td className="px-8 py-6">
                                                <div className="flex items-center gap-4">
                                                    <div>
                                                        <p className="font-black text-sm text-[#18216D]">{student.name}</p>
                                                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-0.5">
                                                            {student.student_id}
                                                        </p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-8 py-6">
                                                <div className="flex items-center gap-3">
                                                    <div className="flex-1 max-w-[80px] h-2 bg-slate-100 rounded-full overflow-hidden">
                                                        <div
                                                            className={`h-full rounded-full transition-all duration-1000 ${student.attendance_percentage < 75 ? 'bg-rose-500' : 'bg-emerald-500'}`}
                                                            style={{ width: `${student.attendance_percentage}%` }}
                                                        ></div>
                                                    </div>
                                                    <span className={`text-xs font-black ${student.attendance_percentage < 75 ? 'text-rose-600' : 'text-slate-600'}`}>
                                                        {student.attendance_percentage}% ({student.absent_days || 0})
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-8 py-6">
                                                <div className="flex flex-col gap-3 min-w-[180px]">
                                                    {student.parents && student.parents.length > 0 ? (
                                                        <>
                                                            {/* Relationship Toggle: More visible pill design */}
                                                            {student.parents && student.parents.length > 1 && (
                                                                <div className="flex bg-slate-100/80 p-1 rounded-xl w-fit self-start mb-1">
                                                                    {['FATHER', 'MOTHER'].map(rel => {
                                                                        const exists = student.parents.some(p => p.relationship === rel);
                                                                        if (!exists) return null;

                                                                        const isSelected = (selectedParents[student.id] ||
                                                                            (student.parents.find(p => p.relationship === 'FATHER') ? 'FATHER' : 'MOTHER')) === rel;

                                                                        return (
                                                                            <button
                                                                                key={rel}
                                                                                onClick={() => setSelectedParents(prev => ({ ...prev, [student.id]: rel }))}
                                                                                className={`px-3 py-1 rounded-lg text-[10px] font-black transition-all duration-300 ${isSelected
                                                                                    ? 'bg-white text-[#18216D] shadow-md ring-1 ring-black/5'
                                                                                    : 'text-slate-400 hover:text-slate-600'
                                                                                    }`}
                                                                            >
                                                                                {rel === 'FATHER' ? 'Father' : 'Mother'}
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}

                                                            {(() => {
                                                                const preferredRel = selectedParents[student.id] ||
                                                                    (student.parents.find(p => p.relationship === 'FATHER') ? 'FATHER' : 'MOTHER');
                                                                const parent = student.parents.find(p => p.relationship === preferredRel) || student.parents[0];

                                                                return (
                                                                    <div className="flex flex-col gap-1.5 animate-in fade-in slide-in-from-left-2 duration-300">
                                                                        <div className="flex items-center gap-2">
                                                                            <p className="text-[11px] font-black text-[#18216D] leading-none mb-0.5">{parent.name}</p>
                                                                            {student.parents?.length === 1 && (
                                                                                <span className="text-[7px] font-black bg-slate-50 text-slate-400 border border-slate-100 px-1.5 py-0.5 rounded-full uppercase tracking-tighter">
                                                                                    {parent.relationship || 'Guardian'}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <div className="space-y-0.5">
                                                                            <div className="flex items-center gap-1.5 text-slate-400">
                                                                                <div className="w-1.5 h-1.5 rounded-full border border-slate-300"></div>
                                                                                <p className="text-[10px] font-bold lowercase tracking-tight">{parent.email}</p>
                                                                            </div>
                                                                            <div className="flex items-center gap-1.5 text-slate-500">
                                                                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400/20 border border-emerald-400/40"></div>
                                                                                <p className="text-[10px] font-black tracking-tight">{parent.phone || 'N/A'}</p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })()}
                                                        </>
                                                    ) : (
                                                        <div className="flex flex-col gap-1 opacity-20">
                                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">No Guardian Info</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-8 py-6">
                                                <div className="flex flex-col">
                                                    <span className={`text-[10px] font-black uppercase tracking-widest ${student.career_pathway ? 'text-[#18216D]' : 'text-slate-300 italic'}`}>
                                                        {student.career_pathway ? student.career_pathway.split('(')[0].trim() : 'N/A'}
                                                    </span>
                                                    {student.grade_level_name && (
                                                        <span className="text-[8px] font-bold text-slate-400 uppercase tracking-tighter">
                                                            {student.grade_level_name}
                                                            {student.stream_name ? ` - ${student.stream_name}` : ''}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-8 py-6">
                                                <div className="flex flex-col gap-1">
                                                    {(() => {
                                                        const data = getLevelData(student.average_performance);
                                                        return (
                                                            <span className={`w-fit ml-1 text-sm font-black uppercase tracking-widest ${data.color}`}>
                                                                {student.average_performance || 'N/A'}
                                                            </span>
                                                        );
                                                    })()}
                                                    {student.average_numeric > 0 && (
                                                        <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest ml-1">{student.average_numeric} Avg Score</p>
                                                    )}
                                                </div>
                                            </td>
                                            {is_principal && (
                                                <td className="px-8 py-6">
                                                    {(() => {
                                                        const bal = balanceMap[String(student.id)];
                                                        if (bal === undefined) return <span className="text-[10px] text-slate-300 font-bold">—</span>;
                                                        if (bal === null) return <span className="text-[10px] text-slate-300 font-bold italic">N/A</span>;
                                                        return (
                                                            <span className={`text-xs font-black ${bal > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                                                                {bal > 0 ? `KES ${bal.toLocaleString()}` : bal < 0 ? `-KES ${Math.abs(bal).toLocaleString()}` : 'Cleared'}
                                                            </span>
                                                        );
                                                    })()}
                                                </td>
                                            )}
                                            <td className="px-8 py-6 text-right">
                                                <div className="flex justify-end gap-2">
                                                    <button
                                                        onClick={(e) => {
                                                            navigate(`/teacher/student-detail/${student.id}`, { 
                                                                state: { 
                                                                    ...location.state,
                                                                    studentIds: students.map(s => s.id),
                                                                    returnUrl: location.pathname + location.search 
                                                                } 
                                                            });
                                                        }}
                                                        className="p-3 bg-white border border-slate-100 text-[#18216D] rounded-xl hover:bg-indigo-50 hover:text-indigo-600 transition-all shadow-sm group/btn"
                                                        title="View Full Profile"
                                                    >
                                                        <EyeIcon className="w-5 h-5" />
                                                    </button>
                                                    {!isHOS && (
                                                        <button
                                                            onClick={() => handleRemoveFromClass(student)}
                                                            className="p-3 bg-white border border-slate-100 text-slate-300 rounded-xl hover:bg-rose-50 hover:text-rose-600 transition-all shadow-sm"
                                                            title="Remove from Class"
                                                        >
                                                            <TrashIcon className="w-5 h-5" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="py-24 text-center">
                                            <UserGroupIcon className="w-16 h-16 text-slate-100 mx-auto mb-4" />
                                            <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em]">No scholars match your criteria</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="px-8 py-6 bg-[#F8FAFC] border-t border-slate-100 flex items-center justify-between">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
                                Showing page {currentPage} of {totalPages}
                            </p>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    className="p-3 bg-white border border-slate-100 rounded-xl text-slate-400 hover:text-[#18216D] disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
                                >
                                    <ChevronLeftIcon className="w-5 h-5" />
                                </button>
                                <div className="flex gap-1">
                                    {[...Array(totalPages)].map((_, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setCurrentPage(i + 1)}
                                            className={`w-10 h-10 rounded-xl text-[10px] font-black transition-all ${currentPage === i + 1
                                                ? 'bg-[#18216D] text-white shadow-lg'
                                                : 'bg-white text-slate-400 hover:text-[#18216D] border border-slate-50 shadow-sm'
                                                }`}
                                        >
                                            {i + 1}
                                        </button>
                                    ))}
                                </div>
                                <button
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                    className="p-3 bg-white border border-slate-100 rounded-xl text-slate-400 hover:text-[#18216D] disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
                                >
                                    <ChevronRightIcon className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                ) : (
                    <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 overflow-hidden">
                        {matrixLoading ? (
                            <div className="py-24 text-center">
                                <ArrowPathIcon className="w-10 h-10 text-indigo-500 animate-spin mx-auto mb-4" />
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Loading Matrix Data...</p>
                            </div>
                        ) : matrixData && matrixData.students ? (
                            <div className="overflow-x-auto relative">
                                <table className="w-full text-left whitespace-nowrap">
                                    <thead className="bg-[#F8FAFC]">
                                        <tr>
                                            <th className="w-48 min-w-[12rem] max-w-[14rem] px-6 py-5 text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] sticky left-0 z-20 bg-[#F8FAFC] border-r border-slate-100 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)]">Scholar</th>
                                            <th className="px-6 py-5 text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] bg-[#F8FAFC] border-r border-slate-100 text-center">Overall</th>
                                            {matrixData.subjects.map(sub => (
                                                <th key={sub.code} className="px-6 py-5 text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] text-center">{sub.name}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {matrixData.students.length > 0 ? (
                                            [...matrixData.students]
                                            .sort((a, b) => (b.overall_pct || 0) - (a.overall_pct || 0))
                                            .map((student, idx) => {
                                                const overallLvl = getLevelData(student.overall_level);
                                                return (
                                                    <tr key={student.id} className="hover:bg-slate-50/50 transition-colors group">
                                                        <td className="w-48 min-w-[12rem] max-w-[14rem] px-6 py-4 sticky left-0 z-10 bg-white group-hover:bg-slate-50 border-r border-slate-100 transition-colors shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)] truncate">
                                                            <div className="flex items-center gap-3">
                                                                <span className="text-[9px] font-black text-slate-300 w-4 text-right shrink-0">{idx + 1}.</span>
                                                                <div className="min-w-0 flex-1">
                                                                    <p className="font-black text-xs text-[#18216D] truncate">{student.name}</p>
                                                                    <p className="text-[8px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-0.5 truncate">
                                                                        {student.student_id}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 border-r border-slate-100 text-center">
                                                            <div className={`flex flex-col items-center justify-center`}>
                                                                <span className={`text-base font-black uppercase tracking-widest ${overallLvl.color}`}>{student.overall_level !== 'N/A' ? student.overall_level : '-'}</span>
                                                                {student.overall_pct > 0 && <span className={`text-[8px] font-bold ${overallLvl.color} opacity-70`}>{student.overall_pct.toFixed(1)}%</span>}
                                                            </div>
                                                        </td>
                                                        {matrixData.subjects.map(sub => {
                                                            const sScore = student.subject_scores[sub.code];
                                                            if (!sScore || sScore.level === 'N/A') {
                                                                return <td key={sub.code} className="px-6 py-4 text-center text-slate-200 text-[10px] font-bold">-</td>;
                                                            }
                                                            const subLvl = getLevelData(sScore.level);
                                                            return (
                                                                <td key={sub.code} className="px-6 py-4 text-center">
                                                                    <div className={`flex flex-col items-center justify-center`}>
                                                                        <span className={`text-base font-black uppercase tracking-widest ${subLvl.color}`}>{sScore.level}</span>
                                                                        {sScore.percentage !== null && <span className={`text-[8px] font-bold ${subLvl.color} opacity-70`}>{sScore.percentage.toFixed(1)}%</span>}
                                                                    </div>
                                                                </td>
                                                            );
                                                        })}
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan={matrixData.subjects.length + 2} className="py-12 text-center text-[10px] font-black text-slate-300 uppercase tracking-[0.2em]">No performance data available</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="py-24 text-center">
                                <ExclamationTriangleIcon className="w-10 h-10 text-slate-300 mx-auto mb-4" />
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Failed to load matrix data</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {isFormatModalOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="bg-white rounded-[2.5rem] w-full max-w-xl shadow-2xl border border-white/20 animate-in zoom-in-95 duration-300 overflow-y-auto max-h-[90vh] custom-scrollbar">
                        
                        {/* Modal Header */}
                        <div className="px-8 pt-8 pb-4 flex items-center justify-between">
                            <div>
                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.3em]">
                                    {exportStep === 'format' ? 'Step 1 of 2' : `Step 2 of 2 · ${exportFormat === 'pdf' ? 'PDF Report' : exportFormat === 'excel' ? 'Excel Sheet' : 'Bulk Report Cards'}`}
                                </p>
                                <h3 className="text-xl font-black text-[#18216D] uppercase tracking-tight mt-1">
                                    {exportStep === 'format' ? 'Choose Export Format' : 'Configure Filters'}
                                </h3>
                            </div>
                            <button
                                onClick={() => { setIsFormatModalOpen(false); setExportStep('format'); setExportFormat(null); }}
                                className="p-2 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-all"
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Step 1: Format Selection */}
                        {exportStep === 'format' && (
                            <div className="px-8 pb-8 space-y-3 mt-2">
                                {[
                                    {
                                        id: 'pdf',
                                        label: 'PDF Report',
                                        desc: 'Performance matrix as a formatted print document',
                                        icon: (
                                            <svg className="w-7 h-7 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                            </svg>
                                        ),
                                        accent: 'hover:border-rose-100 hover:shadow-rose-500/10 group-hover:bg-rose-50',
                                    },
                                    {
                                        id: 'excel',
                                        label: 'Excel Sheet',
                                        desc: 'Raw performance data for further analysis',
                                        icon: (
                                            <svg className="w-7 h-7 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                        ),
                                        accent: 'hover:border-emerald-100 hover:shadow-emerald-500/10 group-hover:bg-emerald-50',
                                    },
                                    {
                                        id: 'zip',
                                        label: 'Bulk Report Cards',
                                        desc: 'Individual PDF report cards for each student — bundled as ZIP',
                                        icon: <ArrowDownTrayIcon className="w-7 h-7 text-indigo-500" />,
                                        accent: 'hover:border-indigo-100 hover:shadow-indigo-500/10 group-hover:bg-indigo-50',
                                    },
                                ].map(fmt => (
                                    <button
                                        key={fmt.id}
                                        onClick={() => { setExportFormat(fmt.id); setExportFilters({ termId: '', careerPathway: '', performanceLevel: '', attendanceLevel: '', scope: 'all', selectedStudentIds: [], houseId: '', reportType: 'all', learningAreaId: '', reportPeriod: 'end_term', examType: '', classFilter: '' }); setExportStep('filters'); }}
                                        className={`group w-full flex items-center gap-4 p-5 bg-slate-50 hover:bg-white border hover:shadow-xl rounded-2xl transition-all active:scale-[0.99] text-left ${fmt.accent}`}
                                    >
                                        <div className={`p-3 bg-white rounded-xl w-fit transition-colors shadow-sm ${fmt.accent.split(' ').find(c => c.includes('group-hover'))}`}>
                                            {fmt.icon}
                                        </div>
                                        <div className="flex-1">
                                            <p className="font-black text-sm text-[#18216D] uppercase tracking-wide">{fmt.label}</p>
                                            <p className="text-[10px] font-bold text-slate-400 mt-0.5">{fmt.desc}</p>
                                        </div>
                                        <ChevronNext className="w-4 h-4 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-0.5 transition-all" />
                                    </button>
                                ))}
                                <button
                                    onClick={() => setIsFormatModalOpen(false)}
                                    className="w-full py-3 text-[10px] font-black uppercase tracking-[0.3em] text-slate-300 hover:text-slate-500 transition-colors mt-2"
                                >
                                    Cancel
                                </button>
                            </div>
                        )}

                        {/* Step 2: Filters */}
                        {exportStep === 'filters' && (
                            <div className="px-8 pb-8">
                                <div className="bg-slate-50 rounded-2xl p-5 space-y-4 mt-2 mb-6">
                                    {/* Shared filters: PDF & Excel */}
                                    {(exportFormat === 'pdf' || exportFormat === 'excel') && (
                                        <>
                                            <div className="grid grid-cols-2 gap-3">
                                                <div className="col-span-2">
                                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] block mb-1.5">Academic Term</label>
                                                    <select
                                                        value={exportFilters.termId}
                                                        onChange={e => setExportFilters(f => ({ ...f, termId: e.target.value }))}
                                                        className="w-full px-3 py-2.5 bg-white border border-slate-100 rounded-xl text-[11px] font-bold text-[#18216D] shadow-sm focus:ring-2 focus:ring-indigo-500/10"
                                                    >
                                                        <option value="">Active Term (default)</option>
                                                        {Array.isArray(terms) && terms.map(t => (
                                                            <option key={t.id} value={t.id}>{t.name} {t.year_name ? `— ${t.year_name}` : ''}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className={(canFilterByClass && exportFormat === 'excel') ? "col-span-1" : "col-span-2"}>
                                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] block mb-1.5">Exam Type</label>
                                                    <select
                                                        value={exportFilters.examType}
                                                        onChange={e => setExportFilters(f => ({ ...f, examType: e.target.value }))}
                                                        className="w-full px-3 py-2.5 bg-white border border-slate-100 rounded-xl text-[11px] font-bold text-[#18216D] shadow-sm focus:ring-2 focus:ring-indigo-500/10"
                                                    >
                                                        <option value="">Combined (All)</option>
                                                        <option value="mid-term">Mid-Term Only</option>
                                                        <option value="end-term">End-Term Only</option>
                                                    </select>
                                                </div>
                                                {(canFilterByClass && exportFormat === 'excel') && (
                                                    <div className="col-span-1">
                                                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] block mb-1.5">Grade / Stream</label>
                                                        <FilterDropdown
                                                            value={exportFilters.classFilter}
                                                            onChange={(val) => setExportFilters(f => ({ ...f, classFilter: val }))}
                                                            options={classOptions}
                                                            placeholder="All Classes"
                                                            className="w-full"
                                                        />
                                                    </div>
                                                )}
                                                {exportFormat === 'pdf' && (
                                                    <div className="col-span-2">
                                                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] block mb-1.5">Report Type</label>
                                                        <select
                                                            value={exportFilters.reportType}
                                                            onChange={e => setExportFilters(f => ({ ...f, reportType: e.target.value }))}
                                                            className="w-full px-3 py-2.5 bg-white border border-slate-100 rounded-xl text-[11px] font-bold text-[#18216D] shadow-sm focus:ring-2 focus:ring-indigo-500/10"
                                                        >
                                                            <option value="all">Combined (All Assessments)</option>
                                                            <option value="formative">Formative Assessments Only</option>
                                                            <option value="summative">Summative Assessments Only</option>
                                                        </select>
                                                    </div>
                                                )}
                                                {exportFormat === 'excel' && (
                                                    <div className="col-span-2">
                                                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] block mb-1.5">Learning Area</label>
                                                        {(() => {
                                                            // Deduplicate by code or name
                                                            const uniqueMap = new Map();
                                                            learningAreas.forEach(la => {
                                                                const key = (la.code || la.name || '').toUpperCase();
                                                                if (key && !uniqueMap.has(key)) {
                                                                    uniqueMap.set(key, { name: la.name, value: key });
                                                                }
                                                            });
                                                            const uniqueOptions = Array.from(uniqueMap.values());
                                                            
                                                            return (
                                                                <FilterDropdown 
                                                                    value={exportFilters.learningAreaId} 
                                                                    onChange={(val) => setExportFilters(f => ({ ...f, learningAreaId: val }))}
                                                                    options={uniqueOptions}
                                                                    placeholder="All Learning Areas"
                                                                    className="w-full"
                                                                />
                                                            );
                                                        })()}
                                                    </div>
                                                )}
                                                <div>
                                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] block mb-1.5">Career Pathway</label>
                                                    <select
                                                        value={exportFilters.careerPathway}
                                                        onChange={e => setExportFilters(f => ({ ...f, careerPathway: e.target.value }))}
                                                        className="w-full px-3 py-2.5 bg-white border border-slate-100 rounded-xl text-[11px] font-bold text-[#18216D] shadow-sm focus:ring-2 focus:ring-indigo-500/10"
                                                    >
                                                        <option value="">All Pathways</option>
                                                        <option value="STEM">STEM</option>
                                                        <option value="SOCIAL_SCIENCES">Social Sciences</option>
                                                        <option value="ARTS_SPORTS">Arts & Sports</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] block mb-1.5">Performance Level</label>
                                                    <select
                                                        value={exportFilters.performanceLevel}
                                                        onChange={e => setExportFilters(f => ({ ...f, performanceLevel: e.target.value }))}
                                                        className="w-full px-3 py-2.5 bg-white border border-slate-100 rounded-xl text-[11px] font-bold text-[#18216D] shadow-sm focus:ring-2 focus:ring-indigo-500/10"
                                                    >
                                                        <option value="">All Levels</option>
                                                        <option value="EE">Exceeding (EE)</option>
                                                        <option value="ME">Meeting (ME)</option>
                                                        <option value="AE">Approaching (AE)</option>
                                                        <option value="BE">Below (BE)</option>
                                                    </select>
                                                </div>
                                                <div className="col-span-2">
                                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] block mb-1.5">House Filter</label>
                                                    <select
                                                        value={exportFilters.houseId}
                                                        onChange={e => setExportFilters(f => ({ ...f, houseId: e.target.value }))}
                                                        className="w-full px-3 py-2.5 bg-white border border-slate-100 rounded-xl text-[11px] font-bold text-[#18216D] shadow-sm focus:ring-2 focus:ring-indigo-500/10"
                                                    >
                                                        <option value="">All Houses</option>
                                                        {Array.isArray(houses) && houses.map(h => (
                                                            <option key={h.id} value={h.id}>{h.name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-span-2">
                                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] block mb-1.5">Attendance</label>
                                                    <select
                                                        value={exportFilters.attendanceLevel}
                                                        onChange={e => setExportFilters(f => ({ ...f, attendanceLevel: e.target.value }))}
                                                        className="w-full px-3 py-2.5 bg-white border border-slate-100 rounded-xl text-[11px] font-bold text-[#18216D] shadow-sm focus:ring-2 focus:ring-indigo-500/10"
                                                    >
                                                        <option value="">All Attendance</option>
                                                        <option value="low">Low (&lt;75%)</option>
                                                        <option value="high">High (&gt;90%)</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <p className="text-[9px] text-slate-400 font-bold pt-1">Filters narrow the data included in the exported matrix.</p>
                                        </>
                                    )}

                                    {/* Bulk-specific filters */}
                                    {exportFormat === 'zip' && (
                                        <>
                                            <div className="grid grid-cols-2 gap-3">
                                                <div className="col-span-2">
                                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] block mb-1.5">Academic Term</label>
                                                    <select
                                                        value={exportFilters.termId}
                                                        onChange={e => setExportFilters(f => ({ ...f, termId: e.target.value }))}
                                                        className="w-full px-3 py-2.5 bg-white border border-slate-100 rounded-xl text-[11px] font-bold text-[#18216D] shadow-sm focus:ring-2 focus:ring-indigo-500/10"
                                                    >
                                                        <option value="">Active Term (default)</option>
                                                        {Array.isArray(terms) && terms.map(t => (
                                                            <option key={t.id} value={t.id}>{t.name} {t.year_name ? `— ${t.year_name}` : ''}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-span-2">
                                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] block mb-1.5">Report Period</label>
                                                    <select
                                                        value={exportFilters.reportPeriod}
                                                        onChange={e => {
                                                            const period = e.target.value;
                                                            setExportFilters(f => ({ 
                                                                ...f, 
                                                                reportPeriod: period,
                                                                reportType: (period === 'mid_term' || period === 'end_term') ? 'summative' : f.reportType 
                                                            }));
                                                        }}
                                                        className="w-full px-3 py-2.5 bg-white border border-slate-100 rounded-xl text-[11px] font-bold text-[#18216D] shadow-sm focus:ring-2 focus:ring-indigo-500/10"
                                                    >
                                                        <option value="end_term">End Term Report</option>
                                                        <option value="mid_term">Mid Term Report</option>
                                                    </select>
                                                </div>
                                                <div className="col-span-2">
                                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] block mb-1.5">Report Type</label>
                                                    <select
                                                        value={exportFilters.reportType}
                                                        onChange={e => setExportFilters(f => ({ ...f, reportType: e.target.value }))}
                                                        className="w-full px-3 py-2.5 bg-white border border-slate-100 rounded-xl text-[11px] font-bold text-[#18216D] shadow-sm focus:ring-2 focus:ring-indigo-500/10"
                                                    >
                                                        <option value="all">Combined (All Assessments)</option>
                                                        <option value="formative">Formative Assessments Only</option>
                                                        <option value="summative">Summative Assessments Only</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] block mb-1.5">At-Risk Only (≤ Level)</label>
                                                    <select
                                                        value={exportFilters.performanceLevel}
                                                        onChange={e => setExportFilters(f => ({ ...f, performanceLevel: e.target.value }))}
                                                        className="w-full px-3 py-2.5 bg-white border border-slate-100 rounded-xl text-[11px] font-bold text-[#18216D] shadow-sm focus:ring-2 focus:ring-indigo-500/10"
                                                    >
                                                        <option value="">All Students</option>
                                                        <option value="AE">Approaching & Below (AE–)</option>
                                                        <option value="BE">Below Only (BE)</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] block mb-1.5">Student Scope</label>
                                                    <select
                                                        value={exportFilters.scope}
                                                        onChange={e => setExportFilters(f => ({ ...f, scope: e.target.value, selectedStudentIds: [] }))}
                                                        className="w-full px-3 py-2.5 bg-white border border-slate-100 rounded-xl text-[11px] font-bold text-[#18216D] shadow-sm focus:ring-2 focus:ring-indigo-500/10"
                                                    >
                                                        <option value="all">All Students ({(students || []).length})</option>
                                                        <option value="filtered">Filtered View ({(filteredStudents || []).length})</option>
                                                    </select>
                                                </div>
                                                <div className="col-span-2 pt-4 border-t border-slate-100">
                                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] block mb-1.5">Specific Scholars (Surgical Selection)</label>
                                                    <Combobox 
                                                        multiple 
                                                        value={exportFilters.selectedStudentIds} 
                                                        onChange={(ids) => setExportFilters(f => ({ ...f, selectedStudentIds: ids }))}
                                                    >
                                                        <div className="relative">
                                                            <div className="relative w-full cursor-default overflow-hidden rounded-xl bg-white text-left shadow-sm border border-slate-100 focus:outline-none sm:text-sm">
                                                                <ComboboxInput
                                                                    className="w-full border-none py-2.5 pl-3 pr-10 text-xs font-bold leading-5 text-[#18216D] focus:ring-0"
                                                                    displayValue={() => ''}
                                                                    onChange={(event) => setComboboxQuery(event.target.value)}
                                                                    placeholder="Search scholars to add..."
                                                                />
                                                            </div>
                                                            <ComboboxOptions className="absolute mt-1 max-h-60 w-full overflow-auto rounded-xl bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm z-[110]">
                                                                {(() => {
                                                                    const source = exportFilters.scope === 'filtered' ? (filteredStudents || []) : (students || []);
                                                                    const filteredSource = comboboxQuery === '' 
                                                                        ? source 
                                                                        : source.filter(s => s?.name?.toLowerCase()?.includes(comboboxQuery?.toLowerCase()));
                                                                    
                                                                    return (filteredSource?.length || 0) === 0 ? (
                                                                        <div className="relative cursor-default select-none py-2 px-4 text-slate-400 text-[10px] uppercase font-black">
                                                                            No scholars found.
                                                                        </div>
                                                                    ) : filteredSource.map(student => (
                                                                        <ComboboxOption
                                                                            key={student.id}
                                                                            value={student.id}
                                                                            className={({ active, selected }) =>
                                                                                `relative cursor-default select-none py-2 pl-10 pr-4 transition-colors ${active ? 'bg-indigo-50 text-[#18216D]' : 'text-slate-600'}`
                                                                            }
                                                                        >
                                                                            {({ selected, active }) => (
                                                                                <>
                                                                                    <span className={`block truncate text-[11px] ${selected ? 'font-black text-[#18216D]' : 'font-medium'}`}>
                                                                                        {student.name} <span className="text-[9px] opacity-40 ml-2 font-black italic">{student.student_id}</span>
                                                                                    </span>
                                                                                    {selected ? (
                                                                                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-[#18216D]">
                                                                                            <i className="fas fa-check text-[10px]"></i>
                                                                                        </span>
                                                                                    ) : null}
                                                                                </>
                                                                            )}
                                                                        </ComboboxOption>
                                                                    ));
                                                                })()}
                                                            </ComboboxOptions>
                                                        </div>
                                                    </Combobox>
                                                    {(exportFilters.selectedStudentIds?.length || 0) > 0 && (
                                                        <div className="mt-3 flex flex-wrap gap-1.5">
                                                            {exportFilters.selectedStudentIds.map(id => {
                                                                const s = (students || []).find(x => x.id === id);
                                                                return (
                                                                    <span key={id} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#18216D] text-white rounded-lg text-[9px] font-black uppercase tracking-tighter">
                                                                        {s?.name?.split(' ')[0]}
                                                                        <button 
                                                                            onClick={() => setExportFilters(f => ({ ...f, selectedStudentIds: f.selectedStudentIds.filter(x => x !== id) }))}
                                                                            className="hover:text-rose-400 transition-colors"
                                                                        >
                                                                            <XMarkIcon className="w-3 h-3" />
                                                                        </button>
                                                                    </span>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <p className="text-[9px] text-slate-400 font-bold pt-4 border-t border-slate-50 mt-4">Selected {exportFilters.selectedStudentIds?.length || 'filtered'} scholars will be included in the {exportFilters.selectedStudentIds?.length === 1 ? 'PDF' : 'ZIP'}.</p>
                                        </>
                                    )}
                                </div>

                                {/* Action Buttons */}
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={() => setExportStep('format')}
                                        className="flex-1 py-3.5 bg-slate-50 border border-slate-100 text-slate-500 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-100 transition-all"
                                    >
                                        ← Back
                                    </button>
                                    <button
                                        onClick={() => {
                                            if (exportFormat === 'pdf') handleExportPDF(exportFilters);
                                            else if (exportFormat === 'excel') handleExportExcel(exportFilters);
                                            else if (exportFormat === 'zip') handleExportZip(exportFilters);
                                            setExportStep('format');
                                            setExportFormat(null);
                                        }}
                                        className="flex-[2] py-3.5 bg-[#FFC425] hover:bg-[#FFD455] text-[#18216D] rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-amber-500/20 transition-all active:scale-95 flex items-center justify-center gap-2"
                                    >
                                        <ArrowDownTrayIcon className="w-4 h-4" />
                                        Export Now
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Export Progress Overlay - Luxe Glassmorphic Redesign */}
            {exportProgress.active && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-6 bg-white/40 backdrop-blur-2xl animate-in fade-in duration-500">
                    <div className="w-full max-w-md bg-white rounded-[3rem] p-10 shadow-[0_32px_64px_-16px_rgba(24,33,109,0.15)] border border-white/40 text-center space-y-8 animate-in zoom-in-95 duration-500">
                        <div className="relative w-36 h-36 mx-auto">
                            {/* Outer Glow for Golden Ring */}
                            <div className="absolute inset-0 rounded-full bg-amber-400/10 blur-xl animate-pulse"></div>
                            
                            <svg className="relative w-full h-full rotate-[-90deg]">
                                <circle cx="72" cy="72" r="64" fill="transparent" stroke="#f1f5f9" strokeWidth="12" />
                                <circle
                                    cx="72" cy="72" r="64" fill="transparent"
                                    stroke="#FFC425"
                                    strokeWidth="12"
                                    strokeDasharray={`${2 * Math.PI * 64}`}
                                    strokeDashoffset={`${2 * Math.PI * 64 * (1 - exportProgress.current / (exportProgress.total || 1))}`}
                                    strokeLinecap="round"
                                    className="transition-all duration-700 ease-out"
                                />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span className="text-4xl font-black text-[#18216D] drop-shadow-sm">
                                    {Math.round((exportProgress.current / (exportProgress.total || 1)) * 100)}%
                                </span>
                                <span className="text-[9px] font-black text-amber-600 uppercase tracking-[0.2em] mt-1">Complete</span>
                            </div>
                        </div>

                        <div className="space-y-3">
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-50 rounded-full border border-amber-100">
                                <div className="w-2 h-2 bg-[#FFC425] rounded-full animate-pulse"></div>
                                <span className="text-[10px] font-black text-amber-700 uppercase tracking-widest">Kianda Scholar Sync</span>
                            </div>
                            <h4 className="text-2xl font-black text-[#18216D] uppercase tracking-tight italic">Generating Bulk Export</h4>
                            <div className="h-0.5 w-12 bg-amber-200 mx-auto rounded-full"></div>
                            <p className="text-[11px] font-medium text-slate-500 uppercase tracking-widest">
                                Processing: <span className="font-black text-[#18216D]">{exportProgress.studentName}</span>
                            </p>
                            <p className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">
                                {exportProgress.current} <span className="mx-1">/</span> {exportProgress.total} Scholars
                            </p>
                        </div>

                        <div className="pt-4 border-t border-slate-50">
                            <div className="flex items-center justify-center gap-1.5">
                                <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                                <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                                <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-bounce"></span>
                            </div>
                            <p className="text-[10px] font-black text-[#18216D]/30 uppercase tracking-[0.4em] mt-4">Synthesizing Archive</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Remove Confirmation Modal */}
            {isRemoveModalOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
                        <div className="px-6 pt-6 pb-4 border-b border-slate-100 flex items-center justify-between">
                            <h3 className="text-lg font-black text-[#18216D] uppercase tracking-tight flex items-center gap-2">
                                <ExclamationTriangleIcon className="w-6 h-6 text-rose-500" />
                                Confirm Removal
                            </h3>
                            <button
                                onClick={() => { setIsRemoveModalOpen(false); setStudentToRemove(null); }}
                                className="p-2 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-all"
                                disabled={isRemoving}
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-6">
                            <p className="text-sm font-bold text-slate-600 mb-4">
                                You are about to remove <span className="text-[#18216D]">{studentToRemove?.name}</span> from this stream.
                            </p>
                            <div className="bg-rose-50 border border-rose-100 rounded-xl p-4 flex gap-3">
                                <ExclamationTriangleIcon className="w-5 h-5 text-rose-600 shrink-0" />
                                <p className="text-xs font-black text-rose-800">
                                    This student will be removed from the stream and all associated learning areas. Are you sure you want to proceed?
                                </p>
                            </div>
                        </div>
                        <div className="px-6 py-4 bg-slate-50 flex justify-end gap-3 border-t border-slate-100">
                            <button
                                onClick={() => { setIsRemoveModalOpen(false); setStudentToRemove(null); }}
                                disabled={isRemoving}
                                className="px-5 py-2.5 rounded-xl text-xs font-black text-slate-500 hover:bg-white border border-transparent hover:border-slate-200 transition-all uppercase tracking-widest disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmRemoveStudent}
                                disabled={isRemoving}
                                className="px-5 py-2.5 rounded-xl text-xs font-black text-white bg-rose-600 hover:bg-rose-700 shadow-md shadow-rose-600/20 transition-all uppercase tracking-widest disabled:opacity-50 flex items-center gap-2"
                            >
                                {isRemoving ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Removing...
                                    </>
                                ) : (
                                    <>
                                        <TrashIcon className="w-4 h-4" />
                                        Confirm Remove
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ScholarRoster;
